/* eslint-disable @typescript-eslint/no-empty-function */
import { DecimalPipe, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import localeFr from '@angular/common/locales/fr';
import { ErrorHandler, NgModule, inject, provideAppInitializer } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';
import { InyovaConfigModule, InyovaSharedModule } from '@inyova/inyova-shared';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular';
import { NgxZendeskWebwidgetConfig, NgxZendeskWebwidgetModule } from 'ngx-zendesk-webwidget';

import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { AppRootEffects } from './app.effects';
import { reducer } from './app.reducers';

import { VerifyComponent } from '@app/verify/verify.component';
import { ProfileModule } from '@profile/profile.module';
import { ReportingModule } from '@reporting/reporting.module';
import { ConfigService } from '@shared/services/config.service';
import { HttpRequestInterceptor } from '@shared/services/http.interceptor';
import { Languages } from '@shared/services/language.service';
import { LookUpService } from '@shared/services/lookup.service';
import { TokenService } from '@shared/services/token.service';
import { TranslateReplaceService } from '@shared/services/translate-replace.service';
import { SharedModule } from '@shared/shared.module';
import { StrategyModule } from '@strategy/strategy.module';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { EditPasswordComponent } from './edit-password/edit-password.component'; // JWT
import { GoogleButtonComponent } from './google-button/google-button.component';
import { KidsGuardService } from './guards/kids.guard';
import { RiskRecheckGuardService } from './guards/riskRecheck.guard';
import { SurveyGuardService } from './guards/survey.guard';
import { HomeComponent } from './home/home.component';
import { LoginComponent } from './login/login.component'; // JWT
import { GeneralModule } from './modules/general/general.module';
import { GrowModule } from './modules/grow/grow.module';
import { RegisterComponent } from './register/register.component';
import { RegisterFormComponent } from './register-form/register-form.component';
import { RegisterFriendComponent } from './register-friend/register-friend.component';
import { ResetPasswordComponent } from './reset-password/reset-password.component';
import { environment } from '../environments/environment';

const zendeskDomain = window.location.origin.includes('.ch') ? 'help.inyova.ch' : 'help.inyova.de';

export class NgxZendeskConfig extends NgxZendeskWebwidgetConfig {
  override lazyLoad = true;
  accountUrl = zendeskDomain;
  callback(zE) {
    zE('webWidget', 'setLocale', 'en');
    zE('webWidget', 'hide');
  }
}

registerLocaleData(localeEn, 'en-CH');
registerLocaleData(localeDe, 'de-CH');
registerLocaleData(localeFr, 'fr-CH');
registerLocaleData(localeEn, 'en-DE');
registerLocaleData(localeDe, 'de-CH');

@NgModule({
  declarations: [
    AppComponent,
    RegisterComponent,
    RegisterFriendComponent,
    RegisterFormComponent,
    LoginComponent,
    ResetPasswordComponent,
    EditPasswordComponent,
    HomeComponent,
    GoogleButtonComponent,
    VerifyComponent
  ],
  exports: [GoogleButtonComponent],
  bootstrap: [AppComponent],
  imports: [
    // Inyova modules
    ProfileModule,
    GeneralModule,
    SharedModule,
    StrategyModule,
    GrowModule,
    ReportingModule,
    // End of Inyova modules
    BrowserAnimationsModule,
    BrowserModule,
    AppRoutingModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule.forRoot({
      fallbackLang: Languages.DE_CH,
      loader: {
        provide: TranslateLoader,
        useClass: TranslateReplaceService,
        deps: [HttpClient]
      }
    }),
    NgxZendeskWebwidgetModule.forRoot(NgxZendeskConfig),
    EffectsModule.forRoot([AppRootEffects]),
    StoreModule.forRoot(
      { root: reducer },
      // See: https://ngrx.io/guide/store/configuration/runtime-checks
      {
        runtimeChecks: {
          strictActionImmutability: false,
          strictActionSerializability: false,
          strictStateImmutability: !environment.production,
          strictStateSerializability: false
        }
      }
    ),
    !environment.production ? StoreDevtoolsModule.instrument() : [],
    InyovaSharedModule,
    InyovaConfigModule.forRoot({ url: environment.apiUrl, urlV3: environment.apiUrlV3, urlV4: environment.apiUrlV4 })
  ],
  providers: [
    KidsGuardService,
    LookUpService,
    RiskRecheckGuardService,
    SurveyGuardService,
    DecimalPipe,
    ConfigService,
    provideAppInitializer(() => {
      const initializerFn = (
        (configService: ConfigService) => () =>
          configService.load()
      )(inject(ConfigService));
      return initializerFn();
    }),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false
      })
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
      useValue: undefined
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
    provideAppInitializer(() => {
      inject(TokenService);
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpRequestInterceptor,
      multi: true
    },
    provideHttpClient(withInterceptorsFromDi())
  ]
})
export class AppModule {
  constructor() {}
}
